import i18n, { getCurrentLocale } from '@/i18n'

/**
 * Locale-aware date/time formatting for use outside component setup
 * (helper functions, composables, standalone utils).
 *
 * Why: bare `new Date(x).toLocaleDateString()` formats in the *browser*
 * locale, so a user who picks French in-app but runs an en-US browser sees
 * US-style "4/28/2026" instead of "28/04/2026". These helpers read the app's
 * active locale (`i18n.global.locale`) so dates follow the language switcher.
 */

const localeTag = (): string => (getCurrentLocale() === 'fr' ? 'fr-FR' : 'en-US')

type DateInput = Date | string | number | null | undefined

function toDate(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null
  const d = value instanceof Date ? value : new Date(value)
  return isNaN(d.getTime()) ? null : d
}

export function formatLocaleDate(value: DateInput, options?: Intl.DateTimeFormatOptions): string {
  const d = toDate(value)
  return d ? d.toLocaleDateString(localeTag(), options) : ''
}

export function formatLocaleTime(value: DateInput, options?: Intl.DateTimeFormatOptions): string {
  const d = toDate(value)
  return d ? d.toLocaleTimeString(localeTag(), options) : ''
}

export function formatLocaleDateTime(
  value: DateInput,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(value)
  return d ? d.toLocaleString(localeTag(), options) : ''
}

const startOfDay = (d: Date): number =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()

/**
 * Relative date for list timestamps. Calendar-day based (not elapsed hours):
 *   today      → exact "HH:mm"
 *   yesterday  → "Yesterday at HH:mm" / "Hier à HH:mm"
 *   2–6 days   → "{n}d ago"
 *   ≥7 / older → locale date
 *   future     → "in {n}d" (<7) else locale date
 * Empty input returns '' (callers map that to their own placeholder).
 */
export function formatRelativeDateTime(value: DateInput): string {
  const d = toDate(value)
  if (!d) return ''
  const t = i18n.global.t
  const time = formatLocaleTime(d, { hour: '2-digit', minute: '2-digit' })
  const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(d)) / 86_400_000)

  if (dayDiff === 0) return time
  if (dayDiff === 1) return t('common.yesterdayAt', { time })
  if (dayDiff > 1 && dayDiff < 7) return t('common.daysAgo', { count: dayDiff })
  if (dayDiff < 0) {
    const ahead = Math.abs(dayDiff)
    return ahead < 7 ? t('common.inDays', { count: ahead }) : formatLocaleDate(d)
  }
  return formatLocaleDate(d)
}
